import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { object, string, array } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";
import { RoleSchema } from "../../services/Schemas/Role";
import { RolesListRequest } from "../../services/Endpoints/roles/RolesListEndpoint";
import { SectionListRequest } from "../../services/Endpoints/sections/SectionsListEndpoint";
import SectionSelector from "./SectionSelector";

interface IFormValues {
  caption: string;
  description: string;
  group: string;
  section_ids: number[];
}

interface ICreateRoleFrom {
  roleInfo?: RoleSchema;
  onSuccess: () => void;
}

interface ISection {
  label: string;
  value: string;
  description: string;
  section_key: string;
}

interface IGroupedSections {
  parent: ISection;
  children: ISection[];
}

const groupSections = (items: ISection[]): IGroupedSections[] => {
  const groups: Record<string, IGroupedSections> = {};

  items.forEach(({ label, value, description, section_key }) => {
    const parts = label.split("/");

    const main = parts[1];
    const child = parts.slice(2).join("/") || null;

    if (!groups[main]) {
      groups[main] = {
        parent: {
          label: `${parts[0]}/${parts[1]}`,
          value,
          description,
          section_key,
        },
        children: [],
      };
    }

    if (child) {
      groups[main].children.push({
        label: child,
        value,
        description,
        section_key,
      });
    }
  });

  return Object.values(groups);
};

export default function CreateRoleForm({
  roleInfo,
  onSuccess,
}: ICreateRoleFrom) {
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const [sections, setSections] = useState<ISection[]>([]);
  const [groupedSections, setGroupedSections] = useState<IGroupedSections[]>(
    []
  );

  const [options, setOptions] = useState<number[]>([]);

  const getDetails = async (id: string) => {
    try {
      const roleRequest = new RolesListRequest(protectedInstance);
      const {
        data: { acl_role },
      } = await roleRequest.details(id);

      const selectedIds = acl_role.sections.map(({ id }) => id);
      setOptions(selectedIds);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
    }
  };

  const getData = async () => {
    try {
      const sectionRequest = new SectionListRequest(protectedInstance);
      const {
        data: { acl_sections },
      } = await sectionRequest.send();
      const list = acl_sections.map(
        ({ caption, id, description, section_key }) => ({
          label: caption,
          description,
          section_key,
          value: id.toString(),
        })
      );

      setSections(list);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    roleInfo && getDetails(roleInfo.id.toString());
  }, [roleInfo]);

  useEffect(() => {
    if (sections.length > 0) {
      setGroupedSections(groupSections(sections));
    }
  }, [sections]);

  useEffect(() => {
    formik.setFieldValue("section_ids", options);
  }, [options]);

  const loginSchema = object({
    caption: string().min(4).required(),
    group: string().required(),
    section_ids: array().required(),
    description: string().optional(),
  });

  const formik = useFormik<IFormValues>({
    initialValues: {
      caption: roleInfo?.caption ?? "",
      description: roleInfo?.description ?? "",
      group: "admin",
      section_ids: [],
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      try {
        const req = new RolesListRequest(protectedInstance);

        roleInfo?.id
          ? await req.update(roleInfo.id.toString(), values)
          : await req.create(values);

        setWaiting(false);
        formik.resetForm();

        setNotification({
          type: "success",
          message: "نقش با موفقیت ذخیره شد.",
        });

        onSuccess();
      } catch (error) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });

  const toggleParent = useCallback(
    (group: IGroupedSections) => {
      const parentId = Number(group.parent.value);
      const childIds = group.children.map((c) => Number(c.value));
      const all = [parentId, ...childIds];

      // Check if parent + ALL children are already selected
      const isAllChildSelected = all.every((id) =>
        formik.values.section_ids.includes(id)
      );

      if (isAllChildSelected) {
        // If fully selected → unselect parent and all children
        formik.setFieldValue(
          "section_ids",
          formik.values.section_ids.filter((id) => !all.includes(id))
        );
      } else {
        // If not fully selected → select parent + all children
        formik.setFieldValue("section_ids", [
          ...new Set([...formik.values.section_ids, ...all]),
        ]);
      }
    },
    [formik.values.section_ids]
  );

  const toggleChild = useCallback(
    (child: ISection, group: IGroupedSections) => {
      const id = Number(child.value);
      let updated: number[] = [];

      // Toggle current child
      updated = formik.values.section_ids.includes(id)
        ? formik.values.section_ids.filter((x) => x !== id)
        : [...formik.values.section_ids, id];

      const parentId = Number(group.parent.value);
      const childIds = group.children.map((c) => Number(c.value));

      // Check child selection states
      const allChildrenSelected = childIds.every((cid) =>
        updated.includes(cid)
      );
      const noChildSelected = childIds.every((cid) => !updated.includes(cid));

      // If all children selected → select parent
      if (allChildrenSelected && !updated.includes(parentId)) {
        updated.push(parentId);
      }

      // If no child selected → remove parent
      if (noChildSelected) {
        updated = updated.filter((v) => v !== parentId);
      }

      // Update form state
      formik.setFieldValue("section_ids", updated);
    },
    [formik.values.section_ids]
  );

  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <MuiTextField
        width="100%"
        label="نام"
        name="caption"
        value={formik.values.caption}
        handleChange={formik.handleChange}
      />

      <Typography fontWeight={600} fontSize="12px">
        انتخاب دسترسی‌ها
      </Typography>

      {sections.length === 0 ? (
        <div
          style={{
            width: "100%",
            height: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={100} />
        </div>
      ) : null}
      <SectionSelector
        groupedSections={groupedSections}
        toggleChild={toggleChild}
        toggleParent={toggleParent}
        selectedIds={formik.values.section_ids}
      />

      <MuiTextField
        multiline
        width="100%"
        label="توضیحات"
        name="description"
        value={formik.values.description}
        handleChange={formik.handleChange}
      />

      <LoadingButton
        variant="confirm"
        type="submit"
        loading={waiting}
        sx={{ mt: 2 }}
      >
        تایید
      </LoadingButton>
    </Stack>
  );
}
